import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { rebootSiteUnits, editLockSettings, fetchLockSettings } from "actions/Units";
import Tooltip from "components/Tooltip";
import NumberInput from "components/NumberInput";
import SelectInput from "components/SelectInput";
import ButtonContained from "components/ButtonContained";
import LoadingIndicator from "components/LoadingIndicator";

const BLE_HW_VERSION_SETTINGS = {
  "1A": ["timeNotSetFailSecure"],
  "2E": [
    "fireTime",
    "lockLights",
    "ignoreLockStatus",
    "radioStrength",
    "chargeCapOnWake",
    "capFireVoltage",
    "bleTxPower",
    "wakeOnMotion",
    "ignoreSchedules",
    "timeNotSetFailSecure",
    "entryMotionMode",
  ],
  "3E": [
    "fireTime",
    "lockLights",
    "ignoreLockStatus",
    "radioStrength",
    "chargeCapOnWake",
    "capFireVoltage",
    "bleTxPower",
    "wakeOnMotion",
    "ignoreSchedules",
    "timeNotSetFailSecure",
    "entryMotionMode",
  ],
  "4E": ["bleTxPower", "overlockOnly", "delinquent"],
  "3K": [
    "fireTime",
    "radioStrength",
    "bleTxPower",
    "ignoreAccessCodesLeadingZeros",
    "logMotionEvents",
    "ignoreSchedules",
    "allowAllAccessCodes",
    "freeExit",
    "timeNotSetFailSecure",
    "forcedEntryEnabled",
  ],
  "4K": [
    "fireTime",
    "radioStrength",
    "bleTxPower",
    "ignoreAccessCodesLeadingZeros",
    "logMotionEvents",
    "ignoreSchedules",
    "allowAllAccessCodes",
    "freeExit",
    "timeNotSetFailSecure",
    "forcedEntryEnabled",
  ],
};

// Helper function to check if a setting is supported for the current lock
const isSettingSupported = (bleHwVersion, setting) =>
  BLE_HW_VERSION_SETTINGS[bleHwVersion]?.includes(setting);

const LockSettingsSettingsModal = ({ lock }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  
  const lockFireTimeRef = useRef(null);
  const bleTxPowerRef = useRef(null);
  const capFireVoltageRef = useRef(null);
  const lockLightsRef = useRef(null);
  const ignoreLeadingZeroRef = useRef(null);
  const ignoreSchedulesRef = useRef(null);
  const forcedEntryRef = useRef(null);
  const logMotionEventsRef = useRef(null);
  const ignoreLockStatusRef = useRef(null);
  const chargeCapOnWakeRef = useRef(null);
  const wakeOnMotionRef = useRef(null);
  const entryMotionModeRef = useRef(null);
  const timeNotSetFailSecureRef = useRef(null);
  const overlockOnlyRef = useRef(null);
  const fetchedLock = useSelector(state => state.units.unitObject?.unit?.locks.find(l => l.id === lock.id));
  const lockSettings = useSelector(state => state.units.lockSettings);
  const authUser = useSelector(state => state.auth.authUser);
  const [hasChanges, setHasChanges] = useState(false);
  const [localLockSettings, setLocalLockSettings] = useState(null);

  useEffect(() => {
    dispatch(fetchLockSettings(lock.uuid));
  }, [])


  useEffect(() => {
    if (lockSettings) {
      setLocalLockSettings(lockSettings);
    }
  },[lockSettings])

  useEffect(() => {
    if (localLockSettings && lockSettings) {
      const isChanged = JSON.stringify(localLockSettings) !== JSON.stringify(lockSettings);
      setHasChanges(isChanged);
    }
  }, [localLockSettings]);

  const handleInputChange = (field, value) => {
    setLocalLockSettings((prevSettings) => ({
      ...prevSettings,
      [field]: value,
    }));
  };

  return (
    fetchedLock && lockSettings ? <div className="min-h-[300px] max-h-[60vh] flex flex-col justify-between px-10">
      <div className="grid grid-cols-2 gap-8 py-4">
        {isSettingSupported(fetchedLock?.bleHwVersion, "fireTime") && <>
          <div ref={lockFireTimeRef}>
            <NumberInput
              id="Lock-Settings-Fire-Time-Input"
              dataCy="Lock-Settings-Fire-Time-Input"
              placeholder={`${intl.formatMessage({ id: "form.lockFireTime" })} (0-255)`}
              onChange={(e) => handleInputChange("fireTime", e.target.value)}
              value={localLockSettings?.fireTime ?? ""}
              min={0}
              max={255}
            />
          </div>
          <Tooltip
            anchorRef={lockFireTimeRef}
            text={intl.formatMessage({ id: "form.lockFireTimeDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "bleTxPower") && <>
          <div ref={bleTxPowerRef}>
            <NumberInput
              id="Lock-Settings-Transmit-Power-Input"
              dataCy="Lock-Settings-Transmit-Power-Input"
              placeholder={`${intl.formatMessage({ id: "form.lockBleTxPower" })} (0-6)`}
              onChange={(e) => handleInputChange("bleTxPower", e.target.value)}
              value={localLockSettings?.bleTxPower ?? ""}
              min={0}
              max={6}
            />
          </div>
          <Tooltip
            anchorRef={bleTxPowerRef}
            text={intl.formatMessage({ id: "form.lockBleTxPowerDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "capFireVoltage") && <>
          <div ref={capFireVoltageRef}>
            <NumberInput
              id="Lock-Settings-Capacitor-Fire-Voltage-Input"
              dataCy="Lock-Settings-Capacitor-Fire-Voltage-Input"
              placeholder={`${intl.formatMessage({ id: "form.capFireVoltage" })} (0-50)`}
              onChange={(e) => handleInputChange("capFireVoltage", e.target.value)}
              value={localLockSettings?.capFireVoltage ?? ""}
              min={0}
              max={50}
            />
          </div>
          <Tooltip
            anchorRef={capFireVoltageRef}
            text={intl.formatMessage({ id: "form.capFireVoltageDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "overlockOnly") && authUser?.featureflagAssignments?.includes('ION_overlock_only') && <>
          <div ref={overlockOnlyRef}>
            <SelectInput
              id="Lock-Settings-Overlock-Only-Input"
              dataCy="Lock-Settings-Overlock-Only-Input"
              value={localLockSettings?.overlockOnly ?? false}
              placeholder={intl.formatMessage({ id: "form.overlockOnly" })}
              onChange={(e) => handleInputChange("overlockOnly", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={overlockOnlyRef}
            text={intl.formatMessage({ id: "form.overlockOnlyDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "lockLights") &&  <>
          <div ref={lockLightsRef}>
            <SelectInput
              id="Lock-Settings-Lock-Lights-Input"
              dataCy="Lock-Settings-Lock-Lights-Input"
              value={localLockSettings?.lockLights ?? false}
              placeholder={intl.formatMessage({ id: "form.lockLights" })}
              onChange={(e) => handleInputChange("lockLights", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={lockLightsRef}
            text={intl.formatMessage({ id: "form.lockLightsDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "ignoreAccessCodesLeadingZeros") &&  <>
          <div ref={ignoreLeadingZeroRef}>
            <SelectInput
              id="Lock-Settings-Ignore-AC-Leading-Zeros-Input"
              dataCy="Lock-Settings-Ignore-AC-Leading-Zeros-Input"
              value={localLockSettings?.ignoreAccessCodesLeadingZeros ?? false}
              placeholder={intl.formatMessage({ id: "form.ignoreLeadingZero" })}
              onChange={(e) => handleInputChange("ignoreAccessCodesLeadingZeros", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={ignoreLeadingZeroRef}
            text={intl.formatMessage({ id: "form.ignoreLeadingZeroDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "ignoreSchedules") &&  <>
          <div ref={ignoreSchedulesRef}>
            <SelectInput
              id="Lock-Settings-Ignore-Schedules-Input"
              dataCy="Lock-Settings-Ignore-Schedules-Input"
              value={localLockSettings?.ignoreSchedules ?? false}
              placeholder={intl.formatMessage({ id: "form.ignoreSchedules" })}
              onChange={(e) => handleInputChange("ignoreSchedules", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={ignoreSchedulesRef}
            text={intl.formatMessage({ id: "form.ignoreSchedulesDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "forcedEntryEnabled") &&  <>
          <div ref={forcedEntryRef}>
            <SelectInput
              id="Lock-Settings-Keypad-Forced-Entry-Input"
              dataCy="Lock-Settings-Keypad-Forced-Entry-Input"
              value={localLockSettings?.forcedEntryEnabled ?? false}
              placeholder={intl.formatMessage({ id: "form.keypadForcedEntry" })}
              onChange={(e) => handleInputChange("forcedEntryEnabled", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={forcedEntryRef}
            text={intl.formatMessage({ id: "form.keypadForcedEntryDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "logMotionEvents") &&  <>
          <div ref={logMotionEventsRef}>
            <SelectInput
              id="Lock-Settings-Log-Motion-Events-Input"
              dataCy="Lock-Settings-Log-Motion-Events-Input"
              value={localLockSettings?.logMotionEvents ?? false}
              placeholder={intl.formatMessage({ id: "form.logMotionEvents" })}
              onChange={(e) => handleInputChange("logMotionEvents", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={logMotionEventsRef}
            text={intl.formatMessage({ id: "form.logMotionEventsDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "ignoreLockStatus") &&  <>
          <div ref={ignoreLockStatusRef}>
            <SelectInput
              id="Lock-Settings-Ignore-Lock-Status-Input"
              dataCy="Lock-Settings-Ignore-Lock-Status-Input"
              value={localLockSettings?.ignoreLockStatus ?? false}
              placeholder={intl.formatMessage({ id: "form.ignoreLockStatus" })}
              onChange={(e) => handleInputChange("ignoreLockStatus", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={ignoreLockStatusRef}
            text={intl.formatMessage({ id: "form.ignoreLockStatusDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "chargeCapOnWake") &&  <>
          <div ref={chargeCapOnWakeRef}>
            <SelectInput
              id="Lock-Settings-Charge-Cap-On-Wake-Input"
              dataCy="Lock-Settings-Charge-Cap-On-Wake-Input"
              value={localLockSettings?.chargeCapOnWake ?? false}
              placeholder={intl.formatMessage({ id: "form.chargeCapOnWake" })}
              onChange={(e) => handleInputChange("chargeCapOnWake", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={chargeCapOnWakeRef}
            text={intl.formatMessage({ id: "form.chargeCapOnWakeDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "wakeOnMotion") &&  <>
          <div ref={wakeOnMotionRef}>
            <SelectInput
              id="Lock-Settings-Wake-On-Motion-Input"
              dataCy="Lock-Settings-Wake-On-Motion-Input"
              value={localLockSettings?.wakeOnMotion ?? false}
              placeholder={intl.formatMessage({ id: "form.wakeOnMotion" })}
              onChange={(e) => handleInputChange("wakeOnMotion", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={wakeOnMotionRef}
            text={intl.formatMessage({ id: "form.wakeOnMotionLockDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "entryMotionMode") &&  <>
          <div ref={entryMotionModeRef}>
            <SelectInput
              id="Lock-Settings-Entry-Motion-Mode-Input"
              dataCy="Lock-Settings-Entry-Motion-Mode-Input"
              value={localLockSettings?.entryMotionMode ?? false}
              placeholder={intl.formatMessage({ id: "form.entryMotionMode" })}
              onChange={(e) => handleInputChange("entryMotionMode", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={entryMotionModeRef}
            text={intl.formatMessage({ id: "form.entryMotionModeDesc" })}
          />
        </>}
        {isSettingSupported(fetchedLock?.bleHwVersion, "timeNotSetFailSecure") &&  <>
          <div ref={timeNotSetFailSecureRef}>
            <SelectInput
              id="Lock-Settings-Time-Not-Set-Input"
              dataCy="Lock-Settings-Time-Not-Set-Input"
              value={localLockSettings?.timeNotSetFailSecure ?? false}
              placeholder={intl.formatMessage({ id: "form.timeNotSetFailSecure" })}
              onChange={(e) => handleInputChange("timeNotSetFailSecure", e.target.value === "true")}
              options={[
                {
                  name: intl.formatMessage({ id: "form.enable" }),
                  value: true
                },
                {
                  name: intl.formatMessage({ id: "form.disable" }),
                  value: false
                },
              ]}
            />
          </div>
          <Tooltip
            anchorRef={timeNotSetFailSecureRef}
            text={intl.formatMessage({ id: "form.timeNotSetFailSecureDesc" })}
          />
        </>}
      </div>
      <div className="flex gap-4">
        <div className="flex-1">
          <ButtonContained
            id="Lock-Settings-Save-Changes-Button"
            dataCy="Lock-Settings-Save-Changes-Button"
            text={intl.formatMessage({ id: "comp.button.saveChanges" })}
            type="default"
            disabled={!hasChanges}
            onClick={() => dispatch(
              editLockSettings(
                {
                  ...localLockSettings,
                  uuid: fetchedLock.uuid
                },
                {
                  successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
                }
              )
            )}
          />
        </div>
        <div>
            <ButtonContained
              id="Lock-Settings-Resend-Button"
              dataCy="Lock-Settings-Resend-Button"
              text={intl.formatMessage({ id: "tooltip.resendLockSettings" })}
              type="default"
              onClick={() => dispatch(
                editLockSettings(
                  lockSettings,
                  {
                    successMessage: intl.formatMessage({ id: "notifications.success.edit" }),
                  }
                )
              )}
            />
        </div>
        <div>
            <ButtonContained
              id="Lock-Settings-Reboot-Button"
              dataCy="Lock-Settings-Reboot-Button"
              text={intl.formatMessage({ id: "tooltip.rebootLock" })}
              type="default"
              onClick={() => dispatch(
                rebootSiteUnits(
                  [fetchedLock.uuid],
                  {
                    message: intl.formatMessage({ id: "notifications.success.unitReboot" }),
                  }
                )
              )}
            />
        </div>
      </div>
    </div> : <div className="min-h-[300px] flex justify-center">
      <LoadingIndicator />
    </div>
  )
};

export default LockSettingsSettingsModal;
